import { Box, Skeleton, Typography } from '@customink/pigment-react';
import {
  type ProjectItemDraft,
  useProduct,
} from '@customink/project-service-react-components';
import { FormProvider, useForm } from 'react-hook-form';

import { AddItemColorSwatchGroup } from './AddItemColorSwatchGroup';
import { ProjectItemAddProofImages } from './ProjectItemAddProofImages';
import { ProjectItemEditSizesFields } from './ProjectItemEditSizesFields';
import { SizingGuide } from '../SizingGuide';

import { spacing16, spacing32 } from '@/utils/designTokens';

type SizeQuantityMap = {
  [size: string]: number | undefined;
};

export type AddItemFormData = {
  colorId?: number;
  sizes?: SizeQuantityMap;
};

type ProjectItemAddFormProps = {
  draftProjectItem: ProjectItemDraft;
  onChange: (data: AddItemFormData) => void;
};

export default function ProjectItemAddForm({
  draftProjectItem,
  onChange,
}: ProjectItemAddFormProps) {
  const { data: product } = useProduct({
    colorId: draftProjectItem.product?.colorId,
  });

  const methods = useForm<AddItemFormData>({
    defaultValues: {
      colorId: draftProjectItem.product?.colorId,
      sizes: {},
    },
  });

  methods.watch((data) => {
    const projectItemData: ProjectItemDraft = {
      ...draftProjectItem,
      design: { cid: draftProjectItem.design?.cid },
      product: {
        colorId: data.colorId,
      },
      sizesQuantities: Object.entries(data.sizes || {}).map(
        ([size, quantity]) => ({
          size,
          quantity: quantity || 0,
        }),
      ),
    };
    onChange(projectItemData);
  });

  const selectedSwatchColorId = methods.watch('colorId');

  const handleColorSwatchSelection = (colorId: number) => {
    methods.setValue('colorId', colorId);
  };

  const getFieldName = (size: string) => `sizes.${size}`;

  return (
    <FormProvider {...methods}>
      <form>
        <Box display="flex" flexDirection="column" gap={spacing32}>
          <Box display="flex" flexDirection="column" gap={spacing16}>
            <Typography component="h3" fontWeight="normal" variant="bodyShort2">
              {product?.name || <Skeleton width="80px" />}
            </Typography>
            <ProjectItemAddProofImages
              colorId={selectedSwatchColorId}
              projectItem={draftProjectItem}
            />
            <AddItemColorSwatchGroup
              colorId={draftProjectItem.product?.colorId}
              designCid={draftProjectItem.design?.cid}
              onChange={handleColorSwatchSelection}
            />
          </Box>
          <Box display="flex" flexDirection="column" gap="16px">
            <Box
              display="flex"
              flexWrap="wrap"
              gap="2px 16px"
              justifyContent="space-between"
            >
              <Typography
                component="h3"
                fontSize="18px"
                fontWeight="bold"
                lineHeight="1.25"
                sx={{ textWrap: 'balance' }}
              >
                Enter Quantities and Sizes
              </Typography>
              <SizingGuide
                hasLineup={product?.has_lineup}
                productId={product?.id}
              />
            </Box>
            <ProjectItemEditSizesFields
              getFieldName={getFieldName}
              item={draftProjectItem}
            />
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
}
